import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  model,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { Alert, AlertModule, AlertService, AlertType, LinkModule, ModalModule } from '@michelin/theme';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from '@angular/material/table';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { Fleet } from '../../../shared/models/fleet/fleet';
import { FleetService } from '../../../shared/services/fleet.service';
import { MatDialog } from '@angular/material/dialog';
import { QuantityEditModalComponent } from '../quantity-edit-modal/quantity-edit-modal.component';
import { NavigationConfirmService } from '../../../shared/services/navigation-confirm.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ContextService } from '../../../shared/services/context.service';
import { IModif } from '../../../shared/models/IModif';
import { NoDataComponent } from '../../../shared/components/no-data/no-data.component';
import moment from 'moment/moment';

@Component({
  selector: 'mic-fleet-quantity-table',
  imports: [
    LinkModule,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIcon,
    MatRow,
    MatRowDef,
    MatSort,
    MatSortHeader,
    MatTable,
    TranslatePipe,
    ModalModule,
    AlertModule,
    TranslatePipe,
    NoDataComponent
  ],
  templateUrl: './quantity-table.component.html',
  styleUrl: './quantity-table.component.scss'
})
export class QuantityTableComponent implements OnInit, OnChanges, IModif, OnDestroy {
  ///////////////////////////////////////
  /// Common code between FleetTables ///
  ///////////////////////////////////////

  // Have tried to mutualize it but Angular doesn't handle 2wany bind and similar features with generics
  // If you think you can do better and want to refactor please increment the counter below :)
  // Wasted time try to refactor this : 2h

  /**
   * Datatable config
   */
  nextMonthDate: Date = moment().add(1, 'M').toDate();
  nextMonth = this.nextMonthDate.getMonth() + 1;
  currentYear: number = this.nextMonthDate.getFullYear();
  monthsNumbersToDisplay = Array.from({ length: 12 }, (_, i) => ((this.nextMonth - 1 + i) % 12) + 1);
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new MatTableDataSource<Fleet>();
  columns = ['seat', 'model', 'realQty', ...this.monthsNumbersToDisplay.map((m) => m.toString()), 'action'];
  private _pendingDeletions: number[] = [];

  /**
   * Search parameters (two-way bound in case of rollback with confirm modal)
   */
  siteId: number;
  year = model<number>(this.currentYear);
  @Input() truckModel: string = '';
  private isRollingBackSearchParams = false;

  /**
   * Property and to know if there are modifications pending and data loading status
   */
  isModificationsPending = model<boolean>(false);
  isLoadingData = model<boolean>(false);
  isSavingData = model<boolean>(false);
  @Output() dataLoaded = new EventEmitter<Fleet[]>();

  ngOnInit() {
    this.contextService.siteId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (id !== null && id !== this.siteId) {
        this.siteId = id;
        this.loadFleetData();
      }
    });

    // Set up custom sorting for the table, as some columns are based on nested properties of the data source
    this.dataSource.sortingDataAccessor = (data, col) => {
      if (col === 'seat') {
        return data.truckSpec.seat;
      } else if (col === 'model') {
        return data.truckSpec.model;
      }
    };
  }

  /**
   * Refresh data on search parameters changes
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.year && changes.year.firstChange) {
      this.loadFleetData();
      return;
    }
    if (changes.year || changes.mine) {
      if (this.isRollingBackSearchParams) {
        this.isRollingBackSearchParams = false;
        return;
      }
      this.navigationConfirm.beforeActionWithConfirmation(
        this.getModifStatus(),
        () => {
          this.loadFleetData();
        },
        () => {
          this.isRollingBackSearchParams = true;
          this.year.update(() => changes.year.previousValue);
        }
      );
    }
    if (changes.truckModel) {
      this.dataSource.filter = this.truckModel?.trim().toLowerCase();
    }
  }

  /**
   * Load fleet quantities data from backend
   * @private
   */
  private loadFleetData() {
    if (!this.year() || !this.siteId) return; // Ensure search params are defined, overwise skip loading
    this.isLoadingData.update(() => true);
    this.dataSource.data = [] as Fleet[]; // Clear current data
    this.fleetService
      .getFleetQuantities(this.siteId, this.year(), this.nextMonth)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((fleetData: Fleet[]) => {
        this._pendingDeletions = [];
        this.dataSource.data = fleetData;
        this.dataLoaded.emit(fleetData);
        this.isLoadingData.update(() => false);
        // Reset modifications pending flag, as we just loaded fresh data
        this.isModificationsPending.update(() => false);
        this.dataSource.sort = this.sort;
        this.dataSource.filterPredicate = (data: Fleet, filter: string) => {
          return data.truckSpec.specification.toLowerCase().includes(filter);
        };
        this.dataSource.filter = this.truckModel?.trim().toLowerCase();
      });
  }

  /**
   * Refresh table's data
   */
  public refreshData(): void {
    this.loadFleetData();
  }

  /**
   * Compute the year for a given month number based on the current year and displayed months
   * @param month Month number (1-12)
   * @returns Corresponding year
   */
  getYearForMonth(month: number) {
    let index = this.monthsNumbersToDisplay.findIndex((m) => m === month);
    let year = this.year();
    for (let i = 0; i < index; i++) {
      // If bigger month number found before, it means we passed to next year
      if (this.monthsNumbersToDisplay[i] > month) {
        return year + 1;
      }
    }
    return year;
  }

  /**
   * Get CSS class for a month cell based on whether it's editable
   * @param month Month number (1-12)
   * @param year Year (4 digits)
   * @returns CSS class name or undefined
   */
  getMonthClass(month: number, year: number): string {
    if (!this.isEditableMonth(month, year)) return 'column-quantity-gray';
  }

  /**
   * Determine if a month is editable based on current date
   * @param month Month number (1-12)
   * @param year Year (4 digits)
   * @returns True if editable, false otherwise
   */
  isEditableMonth(month: number, year: number): boolean {
    return year > this.currentYear || (month >= this.nextMonth && year === this.currentYear);
  }

  protected deleteFleet(element: Fleet) {
    this.navigationConfirm.confirmModal(
      'fleet.card.table.delete.confirm.title',
      'fleet.card.table.delete.confirm.description',
      () => {
        this._pendingDeletions.push(element.truckStat.truckStatPk);
        this.dataSource.data = this.dataSource.data.filter((f) => f.truckStat.truckStatPk !== element.truckStat.truckStatPk);
        this.dataSource._updateChangeSubscription();
        this.isModificationsPending.update(() => true);
      },
      () => {
        // Cancel action
      }
    );
  }

  saveModifications(withRecalculation: boolean) {
    this.isSavingData.update(() => true);
    this.saveFleets(withRecalculation);
  }

  private saveFleets(withRecalculation: boolean) {
    this.fleetService
      .saveFleets(this.siteId, this.year(), this.nextMonth, this._pendingDeletions, this.dataSource.data)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isModificationsPending.update(() => false);
          this.isSavingData.update(() => false);
          this.loadFleetData();

          this.alertService.showAlert({
            type: AlertType.success,
            title: this.translate.instant('fleet.card.table.save.successTitle')
          } as Alert);

          if (!withRecalculation) {
            return;
          }

          this.fleetService
            .recalculateForecast(this.siteId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.alertService.showAlert({
                  type: AlertType.success,
                  title: this.translate.instant('sites.seasonality.alerts.recalculateForecast.title'),
                  content: this.translate.instant('sites.seasonality.alerts.recalculateForecast.description')
                } as Alert);
              },
              error: (err) => {
                this.alertService.showAlert({
                  type: AlertType.critical,
                  title: this.translate.instant('sites.seasonality.alerts.recalculateForecast.errorTitle'),
                  content: err?.error ?? err?.error?.message ?? ''
                } as Alert);
              }
            });
        },
        error: (err) => {
          let errorMsg: string | undefined = undefined;
          if (err?.error?.message) {
            errorMsg = `(${err.error.message})`;
          }

          this.alertService.showAlert({
            type: AlertType.critical,
            title: this.translate.instant('fleet.card.table.save.errorTitle'),
            content: `${this.translate.instant('fleet.card.table.save.errorMsg')} ${errorMsg ?? ''}`
          } as Alert);

          this.isSavingData.update(() => false);
        }
      });
  }


  //////////////////////////////////////////
  /// Specific code for Quantities Table ///
  //////////////////////////////////////////
  /**
   * CellModal
   */
  readonly dialog = inject(MatDialog);

  constructor(
    private readonly fleetService: FleetService,
    private navigationConfirm: NavigationConfirmService,
    private alertService: AlertService,
    private translate: TranslateService,
    private destroyRef: DestroyRef,
    private contextService: ContextService
  ) {
    contextService.register(this);
  }

  getModifStatus(): boolean {
    return this.isModificationsPending();
  }

  ngOnDestroy(): void {
    this.contextService.unRegister(this);
  }

  openCellModal(data2Modify: Fleet, monthNumber: number): void {
    const dialogRef = this.dialog.open(QuantityEditModalComponent, {
      data: {
        truckModel: data2Modify.truckSpec.specification,
        month: data2Modify.fcsts[monthNumber]?.monthNumber,
        year: data2Modify.fcsts[monthNumber]?.year,
        modelQuantity: data2Modify.fcsts[monthNumber]?.fleetQty,
        oemArrivals: data2Modify.fcsts[monthNumber]?.newFleetQty
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result === undefined) return; // Modal was closed without changes
        this.dataSource.data = this.dataSource.data.map((item) => {
          if (item.truckSpec.truckSpecPk !== data2Modify.truckSpec.truckSpecPk) {
            return item; // Not the item we modified
          }
          const fcst = item.fcsts[monthNumber];
          if (fcst) {
            fcst.fleetQty = result.modelQuantity;
            fcst.newFleetQty = result.oemArrivals;
          }
          // Compute next month's values
          const monthIdx = this.monthsNumbersToDisplay.findIndex((m) => m === monthNumber);
          if (monthIdx !== -1) {
            for (let idx2Update = monthIdx + 1; idx2Update < this.monthsNumbersToDisplay.length; idx2Update++) {
              const prevMonth = this.monthsNumbersToDisplay[idx2Update - 1];
              const currMonth = this.monthsNumbersToDisplay[idx2Update];
              const prevFcst = item.fcsts[prevMonth];
              const currFcst = item.fcsts[currMonth];
              if (prevFcst && currFcst) {
                currFcst.fleetQty = prevFcst.fleetQty + prevFcst.newFleetQty;
                currFcst.newFleetQty = 0; // Reset OEM arrivals for subsequent months
              }
            }
          }
          this.isModificationsPending.update(() => true);
          return item;
        });
        this.dataSource._updateChangeSubscription();
      });
  }

  public getExistingTruckSpecIds(): number[] {
    return this.dataSource.data.map((fleet) => fleet.truckSpec.truckSpecPk);
  }

  public resetModifications(): void {
    this.loadFleetData();
  }

  public addNewFleets(result: Array<Fleet>) {
    if (!result || result.length === 0) return;
    this.dataSource.data = [...(this.dataSource.data || []), ...result];
    this.dataSource._updateChangeSubscription();
    this.isModificationsPending.update(() => true);
  }
}
